import { useLayoutEffect, useRef, useState } from "react";
import type React from "react";
import {
  type Cell,
  type Row,
  type RowData,
  type Table as TableInstance,
  flexRender,
} from "@tanstack/react-table";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";

interface DataTableCellProps {
  className?: string;
  onClick?: () => void;
}

interface DataTableProps<TData extends RowData> {
  table: TableInstance<TData>;
  noResultsLabel: string;
  getCellProps?: (
    cell: Cell<TData, unknown>,
    rowData: TData,
  ) => DataTableCellProps | undefined;
  wrapperRef?: React.Ref<HTMLDivElement>;
  wrapperClassName?: string;
  virtualizeRows?: boolean;
}

const ESTIMATED_ROW_HEIGHT = 48;

const renderCells = <TData extends RowData>(
  row: Row<TData>,
  getCellProps: DataTableProps<TData>["getCellProps"],
) =>
  row.getVisibleCells().map((cell) => {
    const cellProps = getCellProps?.(cell, row.original);
    return (
      <TableCell
        key={cell.id}
        className={cellProps?.className}
        onClick={cellProps?.onClick}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  });

export const DataTable = <TData extends RowData>({
  table,
  noResultsLabel,
  getCellProps,
  wrapperRef,
  wrapperClassName,
  virtualizeRows = false,
}: DataTableProps<TData>) => {
  const rows = table.getRowModel().rows;
  const colCount = table.getVisibleLeafColumns().length;

  return (
    <Table
      className="bg-card border-border border"
      wrapperRef={wrapperRef}
      wrapperClassName={wrapperClassName}
    >
      <TableHeader className="bg-muted">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      {virtualizeRows && rows.length > 0 ? (
        <VirtualizedTableBody
          rows={rows}
          getCellProps={getCellProps}
          colCount={colCount}
        />
      ) : (
        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <TableRow key={row.id}>{renderCells(row, getCellProps)}</TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={colCount} className="h-24 text-center">
                {noResultsLabel}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      )}
    </Table>
  );
};

interface VirtualizedTableBodyProps<TData extends RowData> {
  rows: Row<TData>[];
  getCellProps: DataTableProps<TData>["getCellProps"];
  colCount: number;
}

const VirtualizedTableBody = <TData extends RowData>({
  rows,
  getCellProps,
  colCount,
}: VirtualizedTableBodyProps<TData>) => {
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const node = tbodyRef.current;
    if (!node) return;
    setScrollMargin(node.getBoundingClientRect().top + window.scrollY);
  }, [rows.length]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 8,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstItem ? firstItem.start - scrollMargin : 0;
  const paddingBottom = lastItem ? totalSize - lastItem.end : 0;

  return (
    <TableBody ref={tbodyRef}>
      {paddingTop > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingTop, padding: 0, border: 0 }}
          />
        </tr>
      )}
      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        if (!row) return null;
        return (
          <TableRow
            key={row.id}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
          >
            {renderCells(row, getCellProps)}
          </TableRow>
        );
      })}
      {paddingBottom > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingBottom, padding: 0, border: 0 }}
          />
        </tr>
      )}
    </TableBody>
  );
};
